import type { ReactNode } from 'react';
import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { QuestionCircleOutlined } from '@ant-design/icons';
import { Popover, Statistic } from 'antd';

import './MetricCard.scss';

type TPosition = {
  top: string; // e.g. 20%
  left: string; // e.g. 30%
};

type TIndicatorPosition = 'left' | 'right' | 'top' | 'bottom';

interface IMetricCardProps {
  title: string;
  children: ReactNode;
  isLongterm?: boolean;
  indicatorPosition?: TIndicatorPosition;
  leftTextArea?: {
    subtitle: string;
    value: string | undefined;
  };
  rightTextArea?: {
    subtitle: string;
    value: string | undefined;
  };
  position?: TPosition;
  tooltipContent?: ReactNode | string;
  ringColor?: string;
}

function chooseCorrectAnimationClass(indicatorPosition: TIndicatorPosition) {
  switch (indicatorPosition) {
    case 'left':
      return 'metric-card--ltr';
    case 'right':
      return 'metric-card--rtl';
    case 'top':
      return 'metric-card--ttb';
    case 'bottom':
      return 'metric-card--btt';
    default:
      return '';
  }
}

function chooseCorrectBorderColorByIndicatorPosition(
  indicatorPosition: TIndicatorPosition,
  ringColor: string | undefined,
) {
  if (!ringColor) return {};
  switch (indicatorPosition) {
    case 'left':
      return {
        borderColor: `transparent ${ringColor} transparent transparent`,
        filter: `drop-shadow(0 0 5px ${ringColor})`,
      };
    case 'right':
      return {
        borderColor: `transparent transparent transparent ${ringColor}`,
        filter: `drop-shadow(0 0 5px ${ringColor})`,
      };
    case 'top':
      return {
        borderColor: `transparent transparent ${ringColor} transparent`,
        filter: `drop-shadow(0 0 5px ${ringColor})`,
      };
    case 'bottom':
      return {
        borderColor: `${ringColor} transparent transparent transparent`,
        filter: `drop-shadow(0 0 5px ${ringColor})`,
      };
    default:
      return {};
  }
}

function calculatePosition(
  position: TPosition,
  indicatorPosition: 'left' | 'right' | 'top' | 'bottom',
) {
  switch (indicatorPosition) {
    case 'left':
      return {
        top: `${position.top}`,
        left: `calc(${position.left} - 24px)`,
      };
    case 'right':
      return {
        top: `${position.top}`,
        left: `calc(${position.left} + 24px)`,
      };
    case 'top':
      return {
        top: `calc(${position.top} - 24px)`,
        left: `${position.left}`,
      };
    case 'bottom':
      return {
        top: `calc(${position.top} + 24px)`,
        left: `${position.left}`,
      };
    default:
      return {
        top: `${position.top}`,
        left: `${position.left}`,
      };
  }
}

function MetricCard({
  title,
  children,
  isLongterm = false,
  indicatorPosition,
  leftTextArea,
  rightTextArea,
  position,
  tooltipContent,
  ringColor,
}: IMetricCardProps) {
  const { t } = useTranslation();
  const [styles, setStyles] = useState({});
  const [isOnCustomPosition, setIsOnCustomPosition] = useState(false);
  const metricCardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!position) return;
    const customStyles = calculatePosition(position, indicatorPosition!);
    setStyles(customStyles);
    setIsOnCustomPosition(true);
  }, [position]);

  return (
    <div
      ref={metricCardRef}
      className={`metric-card-container ${
        indicatorPosition
          ? `metric-card--${indicatorPosition}`
          : 'metric-card--scale'
      } ${
        isOnCustomPosition ? '' : 'metric-card--hidden'
      } ${chooseCorrectAnimationClass(indicatorPosition!)}`}
      style={styles}
    >
      {indicatorPosition && (
        <div
          className={`metric-card__indicator ${
            isLongterm ? `metric-card--${indicatorPosition}-longterm` : ''
          }`}
          style={{
            ...chooseCorrectBorderColorByIndicatorPosition(
              indicatorPosition,
              ringColor,
            ),
          }}
        />
      )}
      <div
        className="metric-card"
        style={
          ringColor
            ? {
                border: `4px solid ${ringColor}`,
                boxShadow: `0 0 5px ${ringColor}`,
                ...(isLongterm && {
                  transform: 'scale(0.8125)',
                }),
              }
            : {}
        }
      >
        {isOnCustomPosition && (
          <>
            <div
              className="metric-card__title"
              style={{ ...(isLongterm && { fontSize: '1.05rem' }) }}
            >
              {title}
              {tooltipContent && (
                <Popover
                  content={
                    <div className="TextEllipsis__content">
                      {tooltipContent}
                    </div>
                  }
                  trigger="hover"
                >
                  <QuestionCircleOutlined />
                </Popover>
              )}
            </div>
            <div
              className="metric-card__chart"
              style={{ ...(isLongterm && { height: '17rem' }) }}
            >
              {children}
              {leftTextArea && rightTextArea && (
                <div className="metric-card__text-area">
                  <div className="metric-card__text-area--left">
                    <Statistic
                      title={leftTextArea.subtitle}
                      value={
                        leftTextArea.value === t('MetricCard.NotNumber') ||
                        !leftTextArea.value
                          ? 'N/A'
                          : leftTextArea.value
                      }
                      loading={leftTextArea.value === undefined}
                    />
                  </div>
                  <div className="metric-card__divider" />
                  <div className="metric-card__text-area--right">
                    <Statistic
                      title={rightTextArea.subtitle}
                      value={
                        rightTextArea.value === t('MetricCard.NotNumber') ||
                        !rightTextArea.value
                          ? 'N/A'
                          : rightTextArea.value
                      }
                      loading={rightTextArea.value === undefined}
                    />
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default MetricCard;
